import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { useContext } from "react";

export enum ProviderGapStatus {
  Danger = 'danger',
  Warning = 'warning',
  Success = 'success',
}

/**
 * Helper function to determine provider gap status based on feature flags.
 * This can be used in non-hook contexts (e.g., class components, callbacks).
 * 
 * @param featureFlags - Set of enabled feature flags
 * @param gainsPercent - Provider gains percentage
 * @param impactsPercent - Provider impacts percentage
 * @param servesPercent - Provider serves percentage
 * @param passPercentage - Pass percentage threshold
 */
export const getProviderGapStatusDecoratorWithFlags = (
  featureFlags: Set<string>,
  gainsPercent: number,
  impactsPercent: number,
  servesPercent: number,
  passPercentage: number,
): ProviderGapStatus => {
  if (featureFlags.has(Features.IMPACT_PROVIDER_DECORATOR_ENHANCEMENT)) {
    if (gainsPercent > 0) {
      if (impactsPercent >= passPercentage) {
        return ProviderGapStatus.Success;
      } else if (impactsPercent >= 25.0) {
        return ProviderGapStatus.Warning;
      }
    } else {
      if (servesPercent >= passPercentage) {
        return ProviderGapStatus.Success;
      } else if (servesPercent >= 25.0) {
        return ProviderGapStatus.Warning;
      }
    }
  } else {
    if (gainsPercent > 0 || impactsPercent < 100) {
      if (impactsPercent >= passPercentage) {
        return ProviderGapStatus.Success;
      } else if (impactsPercent >= 25.0) {
        return ProviderGapStatus.Warning;
      }
    } else {
      if (servesPercent >= passPercentage) {
        return ProviderGapStatus.Success;
      } else if (servesPercent >= 25.0) {
        return ProviderGapStatus.Warning;
      }
    }
  }

  return ProviderGapStatus.Danger;
}

/**
 * React hook to get a provider gap status decorator function.
 * This hook must be called from within a functional component.
 * 
 * @returns A function that determines provider gap status
 */
export const useProviderGapStatusDecorator = () => {
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  
  return (
    gainsPercent: number,
    impactsPercent: number,
    servesPercent: number,
    passPercentage: number,
  ): ProviderGapStatus => {
    return getProviderGapStatusDecoratorWithFlags(
      featureFlags,
      gainsPercent,
      impactsPercent,
      servesPercent,
      passPercentage
    );
  };
}
